import { cn } from '@/lib/utils'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { Button } from '@/components/ui/button'
import {
  CalendarCheck,
  PhoneCall,
  MessageCircle,
  LayoutDashboard,
  Rocket,
  Sparkles,
  ArrowRight,
} from 'lucide-react'

const deliverables = [
  {
    icon: CalendarCheck,
    text: 'Acompanhamento durante 3 meses',
    description:
      'Monitoramento constante do seu progresso para garantir que você não trave em nenhuma etapa.',
  },
  {
    icon: PhoneCall,
    text: 'Calls ilimitadas',
    description:
      'Acesso direto para alinhamento estratégico e correção de rota sempre que precisar.',
  },
  {
    icon: MessageCircle,
    text: 'Dúvidas ilimitadas no WhatsApp',
    description:
      'Suporte ágil na palma da sua mão. Você nunca ficará sem resposta.',
  },
  {
    icon: LayoutDashboard,
    text: 'Implantação de um sistema de gestão próprio',
    description:
      'Receba a estrutura completa para organizar modelos, conversas e faturamento da sua agência.',
  },
  {
    icon: Rocket,
    text: 'Todo passo a passo do zero ao avançado',
    description:
      'Desde a criação das contas até as estratégias de escala milionária.',
  },
  {
    icon: Sparkles,
    text: 'E muito mais...',
    description:
      'Scripts de venda, contratação de chatters, funis de conversão e segredos do algoritmo.',
  },
]

export const DeliverablesSection = () => {
  const { elementRef, isVisible } = useIntersectionObserver({
    threshold: 0.1,
  })

  return (
    <section
      ref={elementRef}
      className="relative bg-[#050505] py-20 md:py-32 overflow-hidden border-t border-white/5"
    >
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-24">
          <span
            className={cn(
              'inline-block text-primary font-bold tracking-[0.2em] uppercase mb-4 text-xs sm:text-sm transition-all duration-700 transform',
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            )}
          >
            O que está incluso
          </span>
          <h2
            className={cn(
              'font-heading font-black text-3xl sm:text-4xl md:text-6xl text-white uppercase leading-none text-balance transition-all duration-700 delay-100 transform',
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            )}
          >
            Arsenal completo para o
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-[#FFD700] block mt-2">
              Seu Sucesso
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto mb-16 md:mb-20">
          {deliverables.map((item, idx) => (
            <div
              key={idx}
              className={cn(
                'group relative bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 hover:bg-white/[0.07] transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-[0_10px_40px_-10px_rgba(255,107,0,0.1)] hover:border-primary/30 flex flex-col items-start gap-4 overflow-hidden',
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12',
              )}
              style={{ transitionDelay: `${200 + idx * 100}ms` }}
            >
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                <item.icon className="w-24 h-24 text-primary rotate-12 transform translate-x-4 -translate-y-4" />
              </div>

              <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <item.icon className="w-6 h-6 md:w-7 md:h-7 text-primary group-hover:text-white transition-colors duration-300" />
              </div>

              <div className="relative z-10">
                <h3 className="font-heading font-bold text-lg md:text-xl text-white mb-2 leading-tight group-hover:text-primary transition-colors duration-300">
                  {item.text}
                </h3>
                <p className="font-sans text-sm md:text-base text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div
          className={cn(
            'flex flex-col items-center text-center transition-all duration-1000 delay-700 transform',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          )}
        >
          <Button
            asChild
            className="bg-primary hover:bg-primary/90 text-white font-heading font-bold text-lg md:text-xl h-14 md:h-[72px] px-8 md:px-12 rounded-xl shadow-glow-orange hover:scale-105 transition-all duration-300 uppercase tracking-wide group"
          >
            <a
              href="https://wa.me/558393917008?text=Fala%20Lucas%2C%20gostaria%20de%20saber%20mais%20sobre%20a%20mentoria%20OFM%20ELITE%21"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3"
            >
              Quero Acessar Tudo Isso
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
          <p className="mt-4 text-xs md:text-sm text-white/40 uppercase tracking-widest font-medium">
            Vagas extremamente limitadas
          </p>
        </div>
      </div>
    </section>
  )
}
